import axios from 'axios';
import crypto from 'crypto-browserify';
import pako from 'pako';
import store from '../../store';
import * as actions from '../../store/actions';
// loadingHandle — singleton exposé par LoadingProvider.
// Permet d'appeler forceHideAll() hors contexte React (sur 401 JWT).
// Est null si appelé avant le mount du Provider (no-op garanti par le try/catch ci-dessous).
import { loadingHandle } from '../../components/ui/Loading/LoadingProvider';
import { extractApiErrorMessage, ApiNotFoundError } from './apiError';
import { readStoredUser, removeStoredUser } from '../auth/userStorage';
import { reportApiError } from './reportApiError';

// ── Politique 404 NOMMÉE (vague 4 lot 3 — la navigation sort du transport) ──
// Le transport ne fait PLUS de window.location.href : sur 404 il throw une
// ApiNotFoundError typée (voir apiError.js). Cette politique, appliquée dans le
// catch central du client, reproduit le comportement historique (redirect home)
// pour tous les appels existants — SAUF si l'appelant passe l'option
// `{ no404Redirect: true }` : il reçoit alors l'ApiNotFoundError et gère lui-même
// (NotFoundBlock, toast, fallback silencieux…). Les anciennes whitelists d'URL
// rustinées dans le transport (fiches /asset/:id et /nft/:id, claim-collectible)
// sont remplacées par cet opt-out posé au call site (AssetService.fetchAsset,
// NftService.fetchNFT, SubscriptionService.claimCollectible) : comportement
// observable inchangé, décision déplacée du transport vers une politique nommée.
export function apply404RedirectPolicy(error, options) {
	if (!(error instanceof ApiNotFoundError)) return false;
	if (options && options.no404Redirect) return false;
	window.location.href = '/';
	return true; // redirigé : l'appelant reçoit le résultat vide historique, pas de throw
}

// VULN-01 — Option B (clé de session) : plus AUCUNE clé symétrique statique dans le bundle.
// Clé PUBLIQUE RSA embarquée (non secrète) → on génère une clé AES-256 aléatoire par chargement de page,
// envoyée RSA-chiffrée (OAEP-SHA256) dans le header X-Enc-Key ; le serveur la déchiffre avec sa clé privée.
const PUBLIC_KEY = (process.env.REACT_APP_RSA_PUBLIC_KEY || '').replace(/\\n/g, '\n');
const sessionKey = crypto.randomBytes(32);

// IMPORTANT — `public-encrypt` (crypto-browserify) IGNORE l'option `oaepHash` et force SHA-1 pour OAEP/MGF1,
// alors que le backend (cryptoMiddleware.getSessionKey) déchiffre en OAEP-SHA256. Résultat : le X-Enc-Key
// était indéchiffrable côté serveur (oaep decoding error) → fallback clé statique → réponses indéchiffrables
// côté client → application sans aucune donnée. On chiffre donc la clé de session via Web Crypto (SubtleCrypto),
// seule implémentation navigateur qui supporte réellement RSA-OAEP-SHA256.
const spkiDerBuffer = () => {
	const b64 = PUBLIC_KEY.replace(/-----[^-]+-----/g, '').replace(/\s+/g, '');
	const bin = atob(b64);
	const u8 = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
	return u8.buffer;
};
const encKeyHeaderPromise = (async () => {
	const pub = await window.crypto.subtle.importKey(
		'spki', spkiDerBuffer(), { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt']
	);
	const enc = new Uint8Array(await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, pub, sessionKey));
	let s = '';
	for (let i = 0; i < enc.length; i++) s += String.fromCharCode(enc[i]);
	return btoa(s);
})();
const server3dntechURL = process.env.REACT_APP_3DNTECH_SERVER;

// FIX 2026-06-06 — race condition globale.
// L'ancien code utilisait `axios.defaults.headers.common["Authorization"]` qui est GLOBAL.
// Quand 3 calls partent en parallèle au mount (ex. fetchAuthorList sur /auth/users + Header.fetchData
// sur /notification/ + Notifications.useEffect sur /notification/), la 1re call peut SUPPRIMER
// le header global (URL /auth qui ne matche pas la whitelist) PENDANT que les 2 autres lisent
// le global pour envoyer leur requête → /notification/ part SANS Authorization → 401 → modal
// SessionTimeout affichée à tort sur un user connecté.
// Fix : Authorization passée PAR REQUÊTE dans le header local (jamais touche axios.defaults).
function shouldAttachAuth(url, hasToken) {
	if (!hasToken) return false;
	// Symétrique de l'ancienne logique de suppression : on N'ATTACHE PAS Authorization si l'URL
	// matche /auth/* SAUF les exceptions (session-check, signout, user/, reset-password, epic)
	// qui exigent le token (réauth endpoints).
	if (url.includes('/auth')) {
		// NOTE: 'user/' (singulier) ne matche PAS '/auth/users/' (pluriel : 'users/' n'inclut pas 'user/').
		// On ajoute 'users/' explicitement pour que l'admin connecté reçoive la projection complète
		// de findAllUser (route verifyTokenOptional : sans token → projection publique VULN-17, jamais 401).
		// EarniConnect (2026-06-08) : les endpoints authentifiés de gestion des wallets liés vivent sous
		// /auth/* et exigent verifyToken (GET /auth/wallets, POST /auth/wallet/{link,switch,key}, DELETE
		// /auth/wallet/:id). Sans token attaché → 401 → silent logout. 'wallet/register' matche aussi mais
		// est inoffensif (aucun _user pendant l'inscription → hasToken=false en amont).
		// url.endsWith('/auth/me') → GET /auth/me ; url.includes('/auth/me/') → PUT /auth/me/alerts-toggle
		// SPEC-2026-06-29-auth-confirm-2fa.v1 : les endpoints 2FA et attache-email vivent sous /auth/* et
		// exigent verifyToken. Sans token attaché → 401 → silent logout (bug observé : ouverture de
		// /setting#two-factor déloguait via GET /auth/2fa/status sans Authorization).
		if (url.includes('session-check') || url.includes('signout') || url.includes('user/') || url.includes('users/') || url.includes('reset-password') || url.includes('epic') || url.includes('wallets') || url.includes('wallet/') || url.includes('wallet-capabilities') || url.includes('/auth/2fa') || url.includes('/auth/attach-email') || url.endsWith('/auth/me') || url.includes('/auth/me/')) {
			return true;
		}
		return false;
	}
	return true;
}

export async function axiosWithToken(url, method, body={}, params={}, extraHeaders={}, options={}) {
	// Hook stub preview UIX (SPEC-2026-06-07) : court-circuite l'appel réseau + chiffrement
	// quand le harnais de preview a posé window.__UIX_PREVIEW_STUB__. Aucun effet en prod
	// (la global n'est jamais posée hors du build preview /uix-preview).
	if (typeof window !== 'undefined' && window.__UIX_PREVIEW_STUB__) {
		const _stub = window.__UIX_PREVIEW_STUB__(url, method, params, body);
		if (_stub !== undefined) return _stub;
	}
	const user = readStoredUser();
	let result = [];
	const authHeader = (user && user.accessToken && shouldAttachAuth(url, true))
		? { Authorization: 'Bearer ' + user.accessToken }
		: {};

	const iv = crypto.randomBytes(16);
	const cipher = crypto.createCipheriv('aes-256-gcm', sessionKey, iv);
	let encryptedBody = cipher.update(JSON.stringify(body), 'utf-8', 'base64');
	encryptedBody += cipher.final('base64');
	const tag = cipher.getAuthTag();

	const encKeyHeader = await encKeyHeaderPromise;
	await axios({
		method: method,
		url: url,
		data: {
			iv: iv.toString('hex'),
			data: encryptedBody.toString('hex'),
			authTag: tag.toString('hex'),
		},
		params: {...params, timestamp: Date.now()},
		headers: { ...authHeader, ...extraHeaders, 'X-Enc-Key': encKeyHeader },
	})
	.then(data => {
		// const privateKey = process.env.REACT_APP_ASYMMETRIC_PRIVATE_KEY;
		// const encryptedKey = Buffer.from(data.data.key, 'hex');
		// const decryptedSymmetricKey = crypto.privateDecrypt(
		// 	{
		// 	  key: privateKey.replace(/\\n/g, '\n'),
		// 	  padding: crypto.constants.RSA_PKCS1_PADDING,
		// 	},
		// 	encryptedKey
		// );
		// Guard 2026-07-02 (Sentry triage F4) — certaines réponses 2xx arrivent sans l'enveloppe
		// AES (route bypass côté serveur ou payload malformé). Sans ce garde,
		// Buffer.from(undefined,'hex') throw TypeError et crashe le handler → white-screen.
		// Fallback = payload brut ; miroir du garde déjà en place sur le chemin erreur (line ~242).
		if (!data?.data?.iv) { result = data?.data; return; }
		const decipher = crypto.createDecipheriv('aes-256-gcm', sessionKey, Buffer.from(data.data.iv, 'hex'));
		decipher.setAuthTag(Buffer.from(data.data.authTag, 'hex'));
		if (data.data.c === 1) {
			// PERF-A — réponse compressée (deflate avant chiffrement côté serveur)
			const decHex = decipher.update(data.data.data, 'base64', 'hex') + decipher.final('hex');
			result = JSON.parse(pako.inflate(Buffer.from(decHex, 'hex'), { to: 'string' }));
		} else {
			let decryptedData = decipher.update(data.data.data, 'base64', 'utf-8');
			decryptedData += decipher.final('utf-8');
			result = JSON.parse(decryptedData);
		}
	}).catch(error => {
		reportApiError(error, { url, method });
		if(error.response) {
			// eslint-disable-next-line
			if(error.response.status == '401') {
				// 401 = token JWT expiré ou invalide (durée 6h).
				// Au lieu d'un déloggage brutal (removeItem + redirect /), on ouvre la
				// SessionTimeoutModal (existante, déjà branchée dans Header) qui informe
				// l'user proprement et lui permet de re-login sans perdre la page courante.
				// + setLoading(false) impératif sinon le LoadingOverlay reste actif PAR-DESSUS
				// la modal et bloque le clic sur "RECONNECT ▸".
				if(!url.includes('/session-check')) {
					// FIX 2026-06-08 — ne déclencher QUE si l'user ÉTAIT authentifié.
					// FIX 2026-06-08 (silent logout) — Au lieu d'ouvrir la SessionTimeoutModal
					// (qui affiche l'UI "connecté" derrière la modale), on clear le state user
					// et on laisse la page se re-rendre en mode visiteur (§ logout wallet).
					// Pages protégées → ProtectedRoute → SessionExpiredOrAccessDenied → redirect home.
					const _u = readStoredUser();
					// FIX 2026-06-30 — présence de session = accessToken, PAS isVerified.
						// SPEC-2026-06-29-auth-confirm-2fa.v1 a fait passer les comptes wallet à isVerified=false
						// (email non encore validé) tout en les laissant pleinement connectés (verifySignature ne
						// checke pas isVerified). isVerified = « peut faire des actions sensibles » (gaté serveur en
						// 403 EMAIL_NOT_VERIFIED), plus « est connecté ». Garder isVerified ici prive les users wallet
						// de la modal de reconnexion sur expiration JWT (401). Signal fiable de session = accessToken.
						const _wasAuth = !!(_u && _u.accessToken);
					if (_wasAuth) {
						try {
							// Mémoriser la page courante pour retour post-reconnexion.
							// Sauvegardé AVANT setUser({}) : une ProtectedRoute re-rend immédiatement
							// et changerait la pathname à '/' avant que le modal puisse la capturer.
							const _curPath = window.location.pathname + window.location.search;
							if (_curPath && _curPath !== '/') {
								try { localStorage.setItem('_redirectAfterLogin', _curPath); } catch(e2) {}
							}
							// 1) Vider l'état user → page re-render en mode visiteur (plus de "connecté" derrière le modal)
							removeStoredUser();
							store.dispatch(actions.setUser({}));
							// 2) Ouvrir le modal de reconnexion → par-dessus la page visiteur
							store.dispatch(actions.setIsTimeout(true));
							// forceHideAll() ferme le LoadingProvider overlay (compteur → 0)
							if (loadingHandle.current) loadingHandle.current();
						} catch(e) {}
					}
				}
				return;
			}
			else if(error.response.status === 404) {
				if(url.includes('/auth/signin')) {
					const decipher = crypto.createDecipheriv('aes-256-gcm', sessionKey, Buffer.from(error.response.data.iv, 'hex'));
					decipher.setAuthTag(Buffer.from(error.response.data.authTag, 'hex'));
					let decryptedData = decipher.update(error.response.data.data, 'base64', 'utf-8');
					decryptedData += decipher.final('utf-8');
					result = JSON.parse(decryptedData);
					result['status'] = error.response.status;
					throw new Error(JSON.stringify(result));
				}
				else {
					// Vague 4 lot 3 — plus AUCUNE navigation ni whitelist d'URL ici : le
					// transport throw une erreur typée ; la décision de rediriger vit dans
					// apply404RedirectPolicy (catch central ci-dessous), surchargeable par
					// l'option {no404Redirect} au call site. Les ex-rustines fiches
					// /asset/:id, /nft/:id et claim-collectible passent par cet opt-out.
					throw new ApiNotFoundError(url);
				}
			}
			else {
				// CART_ITEMS_INVALID (Gate 2) — préserver la structure {invalid, valid} sur l'Error.
				// extractApiErrorMessage ne garde que le message ; les handlers de paiement du panier
				// (CartCheckoutModal Gate 2) ont besoin de la liste `invalid` pour retirer les items
				// devenus indisponibles avant tout débit. On n'altère AUCUN autre flux d'erreur :
				// seul le payload qui ressemble au contrat cart-invalid (HTTP 422 + tableau `invalid`)
				// reçoit ces champs en plus du message inchangé.
				const decorateCartInvalid = (errObj, payload, httpStatus) => {
					if (payload && typeof payload === 'object' && Array.isArray(payload.invalid)) {
						errObj.status = httpStatus;
						errObj.code = payload.code || 'CART_ITEMS_INVALID';
						errObj.invalid = payload.invalid;
						errObj.valid = payload.valid;
					}
					// SPEC-2026-06-29-auth-confirm-2fa.v1 — propage les codes de gate auth/2FA sur l'Error
					// (consommés inline par les flux de paiement pour le challenge TOTP) ET ouvre une modale
					// GLOBALE pour les flux self-contained (ajout email, enrôlement 2FA) — même mécanisme que
					// le 401 → setIsTimeout. Le challenge TOTP_REQUIRED est géré inline (il doit rejouer la
					// requête d'origine avec le code) ; on n'ouvre pas de modale globale pour lui.
					if (payload && typeof payload === 'object' && payload.code) {
						errObj.code = payload.code;
						errObj.status = httpStatus;
						if (payload.hasEmail !== undefined) errObj.hasEmail = payload.hasEmail;
						if (payload.thresholdUsd !== undefined) errObj.thresholdUsd = payload.thresholdUsd;
						if (payload.invalid !== undefined) errObj.invalid = payload.invalid; // true = code TOTP fourni mais faux
						if (payload.code === 'EMAIL_NOT_VERIFIED' || payload.code === 'TOTP_ENROLL_REQUIRED') {
							try { store.dispatch(actions.setAuthGate({ type: payload.code, hasEmail: payload.hasEmail, thresholdUsd: payload.thresholdUsd })); } catch (_e) {}
						}
					}
					return errObj;
				};
				// Guard: some endpoints (e.g. unhandled 500) return plain JSON without AES fields.
				// Buffer.from(undefined) would throw TypeError and crash the entire catch handler.
				if (!error.response?.data?.iv) {
					throw decorateCartInvalid(
						new Error(extractApiErrorMessage(error.response?.data, `Server error (${error.response.status})`)),
						error.response?.data,
						error.response?.status
					);
				}
				const decipher = crypto.createDecipheriv('aes-256-gcm', sessionKey, Buffer.from(error.response.data.iv, 'hex'));
				decipher.setAuthTag(Buffer.from(error.response.data.authTag, 'hex'));
				let decryptedData = decipher.update(error.response.data.data, 'base64', 'utf-8');
				decryptedData += decipher.final('utf-8');
				result = JSON.parse(decryptedData);
				if (url.includes('/auth/signin')) {
					result['status'] = error.response.status;
					throw new Error(JSON.stringify(result));
				}
				throw decorateCartInvalid(
					new Error(extractApiErrorMessage(result)),
					result,
					error.response?.status
				);
			}
		}
		throw error
	})
	// Dernier maillon du catch central : politique 404 nommée (voir apply404RedirectPolicy).
	// Par défaut = redirect home historique (l'appelant reçoit le résultat vide, comme avant) ;
	// opt-out par appel via options.no404Redirect → l'ApiNotFoundError remonte au caller.
	.catch(err => {
		if (apply404RedirectPolicy(err, options)) return;
		throw err;
	});

	return result;
}

export default axiosWithToken;

export async function axiosWithoutEncryption(url, method, body={}, params={}, extraHeaders={}) {
	if (typeof window !== 'undefined' && window.__UIX_PREVIEW_STUB__) {
		const _stub = window.__UIX_PREVIEW_STUB__(url, method, params, body);
		if (_stub !== undefined) return _stub;
	}
	const user = readStoredUser();
	let result = [];
	// FIX race condition globale — Authorization PAR REQUÊTE (idem axiosWithToken).
	// Note : la whitelist d'`axiosWithoutEncryption` est plus restrictive (juste signout + user/),
	// on respecte ce comportement historique via une variante locale de shouldAttachAuth.
	const hasToken = !!(user && user.accessToken);
	const attachAuth = hasToken && (
		!url.includes('/auth') ||
		url.includes('signout') ||
		url.includes('user/')
	);
	const authHeader = attachAuth ? { Authorization: 'Bearer ' + user.accessToken } : {};

	const encKeyHeader = await encKeyHeaderPromise;
	await axios({
		method: method,
		url: url,
		data: body,
		params: {...params, timestamp: Date.now()},
		headers: { ...authHeader, ...extraHeaders, 'X-Enc-Key': encKeyHeader },
	})
	.then(data => {
		result = data.data;
		if(url.includes(server3dntechURL)) {
			return result;
		}
		// Guard 2026-07-02 (Sentry triage F4, miroir axiosWithToken) — 2xx sans enveloppe AES →
		// laisser result = data.data brut au lieu de laisser Buffer.from(undefined,'hex') crasher.
		if (!data?.data?.iv) return;
		const decipher = crypto.createDecipheriv('aes-256-gcm', sessionKey, Buffer.from(data.data.iv, 'hex'));
		decipher.setAuthTag(Buffer.from(data.data.authTag, 'hex'));
		if (data.data.c === 1) {
			// PERF-A — réponse compressée (deflate avant chiffrement côté serveur)
			const decHex = decipher.update(data.data.data, 'base64', 'hex') + decipher.final('hex');
			result = JSON.parse(pako.inflate(Buffer.from(decHex, 'hex'), { to: 'string' }));
		} else {
			let decryptedData = decipher.update(data.data.data, 'base64', 'utf-8');
			decryptedData += decipher.final('utf-8');
			result = JSON.parse(decryptedData);
		}
	}).catch(error => {
		reportApiError(error, { url, method });
		if(url.includes(server3dntechURL)) {
			throw new Error(error.response.data.message);
		}
		// Guard: réponses d'erreur non-chiffrées (ex: 400 "invalid SIWE message format" de verify-signature).
		// Sans ce guard, Buffer.from(undefined) lève TypeError et crashe silencieusement.
		if (!error.response?.data?.iv) {
			throw new Error(extractApiErrorMessage(error.response?.data, `Server error (${error.response?.status})`));
		}
		const decipher = crypto.createDecipheriv('aes-256-gcm', sessionKey, Buffer.from(error.response.data.iv, 'hex'));
		decipher.setAuthTag(Buffer.from(error.response.data.authTag, 'hex'));
		let decryptedData = decipher.update(error.response.data.data, 'base64', 'utf-8');
		decryptedData += decipher.final('utf-8');
		result = JSON.parse(decryptedData);
		throw new Error(extractApiErrorMessage(result));
	});

	return result;
}
// ── axiosMultipart — uploads binaires du client central (vague 4 lot 3) ─────
// Les routes d'upload multipart (/tile/upload-image/, /tile/upload-thumbnail/,
// /bug-reports/upload-image, /nft/:id/custom-image) sont EXCLUES du pipeline
// AES-GCM des deux côtés (server/index.js : decryptBody l.~250 + encryptResponse
// l.~264) : un fichier binaire ne peut pas transiter par l'enveloppe JSON
// chiffrée. Historiquement chaque service dupliquait axios brut + son propre
// header Bearer lu de localStorage (pattern 2 de l'audit P1-8). Centralisé ici :
//  - Authorization PAR REQUÊTE (même fix race 2026-06-06 que axiosWithToken) ;
//  - erreurs homogènes : throw Error(extractApiErrorMessage(...)) comme le reste
//    du client (les callers existants consomment déjà err.message) ;
//  - reportApiError branché (observabilité identique aux autres verbes).
export async function axiosMultipart(url, method, formData, params={}, extraHeaders={}) {
	if (typeof window !== 'undefined' && window.__UIX_PREVIEW_STUB__) {
		const _stub = window.__UIX_PREVIEW_STUB__(url, method, params, formData);
		if (_stub !== undefined) return _stub;
	}
	const user = readStoredUser();
	const authHeader = (user && user.accessToken) ? { Authorization: 'Bearer ' + user.accessToken } : {};
	try {
		const res = await axios({
			method: method,
			url: url,
			data: formData,
			params: params,
			headers: { ...authHeader, ...extraHeaders },
		});
		return res.data;
	} catch (error) {
		reportApiError(error, { url, method });
		throw new Error(extractApiErrorMessage(error.response?.data, `Server error (${error.response?.status})`));
	}
}
